// Dashboard → Score Movement: the payload shape (mirrors score-movement.logic.ts on
// the API — there is no shared package) and the small roll-ups the tab does in the
// browser. Kept out of the component so they can be tested without rendering it.
//
// Division of labour: the SERVER decides what "improved", "declined" and a band move
// mean and stamps the answer on every person, per month. Nothing here re-derives
// those — it only counts and averages what it was handed, so the cards, the charts
// and the drill-down lists cannot disagree with each other.

export type BandKey = 'stars' | 'achievers' | 'strivers' | 'atRisk' | 'other';
export type MoveKey = 'improved' | 'declined' | 'steady' | 'new';

export interface MonthPoint {
  month: string;
  scored: number;
  avg: number | null;
  /**
   * The middle score of the people scored that month, and its change on the month
   * before. Shown beside the average everywhere: a few very low scores pull the average
   * down while the typical person has not moved, and the gap between the two is how a
   * reader sees that. Optional because an API one deploy behind does not send them.
   */
  median?: number | null;
  medianDelta?: number | null;
  /** avg − the previous month's avg: the plain difference of the two numbers on screen. */
  avgDelta: number | null;
  /** Average change for people scored in BOTH months. The figure that ranks teams — a
   *  joiner or a leaver moves avgDelta but cannot move this. */
  sameDelta: number | null;
  bands: Record<BandKey, number>;
  improved: number;
  declined: number;
  steady: number;
  fresh: number;
  bandUp: number;
  bandDown: number;
}

export interface ParamSeries {
  code: string;
  name: string;
  avg: Array<number | null>;
  weight: Array<number | null>;
  pct: Array<number | null>;
}

export interface Series {
  monthly: MonthPoint[];
  params: ParamSeries[];
}

export interface GroupSeries extends Series {
  key: string | null;
  name: string;
  teamSize: number;
}

export interface EmployeeMovement {
  code: string;
  name: string;
  store: string;
  city: string;
  state: string;
  trade: string;
  category: string;
  managerCode: string | null;
  managerName: string | null;
  amCode: string | null;
  amName: string | null;
  thCode: string | null;
  thName: string | null;
  totals: Array<number | null>;
  bands: Array<BandKey | null>;
  moves: Array<MoveKey | null>;
  delta: number | null;
  declineStreak: number;
}

export interface BandTransition {
  from: BandKey | 'new';
  to: BandKey;
  count: number;
}

export interface ScoreMovementData {
  requestedMonth: string;
  anchorMonth: string;
  provisional: boolean;
  minDelta: number;
  partial: boolean;
  notice: string | null;
  tiers: { available: boolean };
  managerRolesExcluded: number;
  months: string[];
  overall: Series;
  transitions: BandTransition[];
  byManager: GroupSeries[];
  byAm: GroupSeries[];
  byTh: GroupSeries[];
  parameters: Array<{ code: string; name: string }>;
  parameterMonths: string[];
  employees: EmployeeMovement[];
}

// ── Labels and colours ───────────────────────────────────────────────────────

/** Bands that are a rank. `other` (unclassified / excluded) is shown only when present. */
export const RANKED_BANDS: readonly BandKey[] = ['stars', 'achievers', 'strivers', 'atRisk'];

// Hex twins of the Employee Score page's QUADRANT_STYLE (emerald / sky / amber / rose),
// so a band is the same colour on both screens.
export const BAND_META: Record<BandKey, { label: string; hex: string; chip: string }> = {
  stars: { label: 'Stars', hex: '#10B981', chip: 'bg-emerald-50 text-emerald-700' },
  achievers: { label: 'Achievers', hex: '#0EA5E9', chip: 'bg-sky-50 text-sky-700' },
  strivers: { label: 'Strivers', hex: '#F59E0B', chip: 'bg-amber-50 text-amber-800' },
  atRisk: { label: 'At Risk', hex: '#F43F5E', chip: 'bg-rose-50 text-rose-700' },
  other: { label: 'Unranked', hex: '#CBD5E1', chip: 'bg-gray-100 text-gray-600' },
};

export const MOVE_META: Record<MoveKey, { label: string; hex: string }> = {
  improved: { label: 'Improved', hex: '#10B981' },
  steady: { label: 'Steady', hex: '#CBD5E1' },
  declined: { label: 'Declined', hex: '#F43F5E' },
  new: { label: 'No previous score', hex: '#93C5FD' },
};

/** 'YYYY-MM-01' → "Aug" / "Aug 26" / "Aug 2026". Built from the parts, not parsed as
 *  a date, so a browser west of UTC cannot roll the first of the month back a day. */
export const monthLabel = (month: string, style: 'short' | 'year' | 'long' = 'year'): string => {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  const d = new Date(y, m - 1, 1);
  if (style === 'short') return d.toLocaleDateString('en-IN', { month: 'short' });
  if (style === 'long') return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

export const fmtScore = (v: number | null | undefined): string => (v == null ? '—' : v.toFixed(1));

/** A signed change: "+1.9", "−2.4", "0.0". A real minus sign, so it lines up with "+". */
export const fmtDelta = (v: number | null | undefined, digits = 1): string => {
  if (v == null) return '—';
  const r = Number(v.toFixed(digits));
  if (r === 0) return (0).toFixed(digits);
  return `${r > 0 ? '+' : '−'}${Math.abs(r).toFixed(digits)}`;
};

export type Tone = 'good' | 'bad' | 'flat';
/** Direction of a change, with a dead band so a rounding wobble is not a trend. */
export const toneOf = (v: number | null | undefined, threshold = 0.05): Tone =>
  v == null || Math.abs(v) < threshold ? 'flat' : v > 0 ? 'good' : 'bad';

export const TONE_TEXT: Record<Tone, string> = {
  good: 'text-emerald-600',
  bad: 'text-rose-600',
  flat: 'text-gray-500',
};

// ── Parameter trend ──────────────────────────────────────────────────────────

export interface ParamTrend {
  code: string;
  name: string;
  /** Latest month that has a value, and the one before it that does. */
  latest: number | null;
  latestIdx: number;
  previous: number | null;
  previousIdx: number;
  change: number | null;
  /** Average contribution and weight in the latest month — "13.1 of 15". */
  points: number | null;
  weight: number | null;
  direction: 'improving' | 'declining' | 'steady' | 'none';
}

/**
 * Where a parameter stands and which way it moved, month on month.
 *
 * Compares the newest month that HAS a value with the one before it that has one —
 * not blindly the last two columns — because parameter history can start part-way
 * through the window. "Improving" needs the same one-point move a person's score does.
 */
export const paramTrendOf = (p: ParamSeries, minDelta: number): ParamTrend => {
  let latestIdx = -1;
  for (let i = p.pct.length - 1; i >= 0; i--) {
    if (p.pct[i] != null) { latestIdx = i; break; }
  }
  let previousIdx = -1;
  for (let i = latestIdx - 1; i >= 0; i--) {
    if (p.pct[i] != null) { previousIdx = i; break; }
  }
  const latest = latestIdx >= 0 ? p.pct[latestIdx] : null;
  const previous = previousIdx >= 0 ? p.pct[previousIdx] : null;
  const change = latest != null && previous != null ? Math.round((latest - previous) * 10) / 10 : null;
  return {
    code: p.code,
    name: p.name,
    latest,
    latestIdx,
    previous,
    previousIdx,
    change,
    points: latestIdx >= 0 ? p.avg[latestIdx] : null,
    weight: latestIdx >= 0 ? p.weight[latestIdx] : null,
    direction: change == null ? 'none' : change >= minDelta ? 'improving' : change <= -minDelta ? 'declining' : 'steady',
  };
};

// ── Median ───────────────────────────────────────────────────────────────────

/**
 * The median of a list of scores to two decimals, or null for an empty list. The same
 * rule as the API's medianOf (score-movement.logic.ts): a COPY is sorted by value, and
 * an even count takes the mean of the two middle scores. Needed here only for the
 * store breakdown, which is grouped in the browser; every other median comes from
 * the API.
 */
export const medianOf = (values: readonly number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  return Math.round(median * 100) / 100;
};

// ── Breakdown by a store attribute ───────────────────────────────────────────

export interface BreakdownRow {
  label: string;
  scored: number;
  avg: number | null;
  /** The group's middle score in the latest month. */
  median: number | null;
  /** Average change for the group's people scored in both months (like-for-like), so
   *  a group is not marked down for gaining a new joiner. */
  delta: number | null;
  improved: number;
  declined: number;
}

/**
 * Latest-month average score per group, with the like-for-like change on the month before.
 *
 * Done here rather than on the server because Region is rolled up from State with a
 * master the browser already holds (useStoreRegions) — the API has no region column.
 * `labelOf` returning '' files the person under `noneLabel`.
 */
export const breakdownBy = (
  employees: EmployeeMovement[],
  labelOf: (e: EmployeeMovement) => string | undefined,
  noneLabel: string,
): BreakdownRow[] => {
  const acc = new Map<string, { scored: number; sum: number; scores: number[]; sameN: number; sameSum: number; improved: number; declined: number }>();
  for (const e of employees) {
    const last = e.totals.length - 1;
    const cur = last >= 0 ? e.totals[last] : null;
    if (cur == null) continue; // not scored in the latest month — nothing to show for it
    const label = (labelOf(e) || '').trim() || noneLabel;
    let a = acc.get(label);
    if (!a) { a = { scored: 0, sum: 0, scores: [], sameN: 0, sameSum: 0, improved: 0, declined: 0 }; acc.set(label, a); }
    a.scored += 1;
    a.sum += cur;
    a.scores.push(cur);
    // e.delta is the server's latest-month change, present only when the person was
    // scored in both months — which is exactly the like-for-like population.
    if (e.delta != null) { a.sameN += 1; a.sameSum += e.delta; }
    if (e.moves[last] === 'improved') a.improved += 1;
    else if (e.moves[last] === 'declined') a.declined += 1;
  }
  const rows: BreakdownRow[] = [];
  for (const [label, a] of acc) {
    rows.push({
      label,
      scored: a.scored,
      avg: Math.round((a.sum / a.scored) * 100) / 100,
      median: medianOf(a.scores),
      delta: a.sameN > 0 ? Math.round((a.sameSum / a.sameN) * 100) / 100 : null,
      improved: a.improved,
      declined: a.declined,
    });
  }
  // Biggest groups first: a one-person city at 100 is not the headline. The
  // "no store" bucket goes last whatever its size.
  return rows.sort((a, b) =>
    Number(a.label === noneLabel) - Number(b.label === noneLabel) || b.scored - a.scored || a.label.localeCompare(b.label),
  );
};

// ── Watchlists ───────────────────────────────────────────────────────────────

/** People with a comparable latest month, biggest rise first. */
export const topGainers = (employees: EmployeeMovement[]): EmployeeMovement[] =>
  employees
    .filter((e) => e.moves[e.moves.length - 1] === 'improved' && e.delta != null)
    .sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0) || a.name.localeCompare(b.name));

/** Biggest fall first. */
export const topDecliners = (employees: EmployeeMovement[]): EmployeeMovement[] =>
  employees
    .filter((e) => e.moves[e.moves.length - 1] === 'declined' && e.delta != null)
    .sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0) || a.name.localeCompare(b.name));

/** Months in a row a person must have fallen to be on the "keeps falling" list. */
export const FALLING_STREAK_MIN = 3;

/** Falling for FALLING_STREAK_MIN+ months running — longest streak first, then the
 *  lowest current score, since that is who is closest to trouble. */
export const stillFalling = (employees: EmployeeMovement[]): EmployeeMovement[] =>
  employees
    .filter((e) => e.declineStreak >= FALLING_STREAK_MIN)
    .sort((a, b) =>
      b.declineStreak - a.declineStreak
      || (a.totals[a.totals.length - 1] ?? 0) - (b.totals[b.totals.length - 1] ?? 0)
      || a.name.localeCompare(b.name),
    );

// ── CSV ──────────────────────────────────────────────────────────────────────

const cell = (v: number | null | undefined, digits = 2): string => (v == null ? '' : v.toFixed(digits));

/**
 * A person's change in month `at` on the month before it.
 *
 * For the latest month this is the server's own `delta`. For an earlier month it is
 * the difference of the two scores in the payload. For the FIRST month of the window
 * it is unknown — the month before it is not sent — so it is null rather than a guess;
 * `moves[0]` still says which way the person went.
 */
export const changeAt = (e: EmployeeMovement, at: number): number | null => {
  if (at === e.totals.length - 1) return e.delta;
  if (at < 1) return null;
  const cur = e.totals[at];
  const prev = e.totals[at - 1];
  return cur != null && prev != null ? Math.round((cur - prev) * 100) / 100 : null;
};

/** `at` is the month the list is ABOUT (default: the latest), so that a list opened
 *  from June's bar exports June's change and standing, not the latest month's. */
export const employeeCsv = (months: string[], rows: EmployeeMovement[], at = months.length - 1): { headers: string[]; rows: string[][] } => {
  const about = at >= 0 ? monthLabel(months[at], 'long') : '';
  return {
    headers: [
      'Employee', 'Employee Code', 'Store', 'City', 'Reporting Manager', 'Assistant Manager', 'Trade Head',
      ...months.map((m) => `Score ${monthLabel(m, 'long')}`),
      `Change (${about})`, 'Previous Standing', `Standing (${about})`, `Movement (${about})`,
    ],
    rows: rows.map((e) => {
      const prevBand = at >= 1 ? e.bands[at - 1] : null;
      const curBand = at >= 0 ? e.bands[at] : null;
      const move = at >= 0 ? e.moves[at] : null;
      return [
        e.name, e.code, e.store, e.city, e.managerName ?? '', e.amName ?? '', e.thName ?? '',
        ...e.totals.map((t) => cell(t)),
        cell(changeAt(e, at)),
        prevBand ? BAND_META[prevBand].label : '',
        curBand ? BAND_META[curBand].label : '',
        move ? MOVE_META[move].label : '',
      ];
    }),
  };
};

export const groupCsv = (nameLabel: string, months: string[], rows: GroupSeries[]): { headers: string[]; rows: string[][] } => ({
  headers: [
    nameLabel, 'Code', 'Team',
    ...months.map((m) => `Avg Score ${monthLabel(m, 'long')}`),
    ...months.map((m) => `Median Score ${monthLabel(m, 'long')}`),
    'Avg Change (Same People)', 'Improved', 'Declined', 'Steady',
    'Stars', 'Achievers', 'Strivers', 'At Risk', 'Moved Up a Band', 'Moved Down a Band',
  ],
  rows: rows.map((g) => {
    const p = g.monthly[g.monthly.length - 1];
    return [
      g.name, g.key ?? '', String(g.teamSize),
      ...g.monthly.map((m) => cell(m.avg)),
      ...g.monthly.map((m) => cell(m.median)),
      cell(p?.sameDelta),
      String(p?.improved ?? 0), String(p?.declined ?? 0), String(p?.steady ?? 0),
      String(p?.bands.stars ?? 0), String(p?.bands.achievers ?? 0), String(p?.bands.strivers ?? 0), String(p?.bands.atRisk ?? 0),
      String(p?.bandUp ?? 0), String(p?.bandDown ?? 0),
    ];
  }),
});

export const monthlyCsv = (series: Series): { headers: string[]; rows: string[][] } => ({
  headers: [
    'Month', 'Employees Scored', 'Average Score', 'Change vs Previous Month', 'Median Score', 'Median Change vs Previous Month',
    'Improved', 'Declined', 'Steady',
    'No Previous Score', 'Stars', 'Achievers', 'Strivers', 'At Risk', 'Unranked', 'Moved Up a Band', 'Moved Down a Band',
  ],
  rows: series.monthly.map((p) => [
    monthLabel(p.month, 'long'), String(p.scored), cell(p.avg), cell(p.avgDelta), cell(p.median), cell(p.medianDelta),
    String(p.improved), String(p.declined), String(p.steady), String(p.fresh),
    String(p.bands.stars), String(p.bands.achievers), String(p.bands.strivers), String(p.bands.atRisk), String(p.bands.other),
    String(p.bandUp), String(p.bandDown),
  ]),
});
